import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import type { CashflowEvent } from "@/features/enterprises/types";
import {
  useGroupCashflow,
  useLatestBalances,
} from "@/features/group_finance/hooks/useGroupFinance";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";

interface Props {
  /** Cashflow zdarzenia symulowanego zlecenia (z silnika symulacji). */
  simEvents: CashflowEvent[];
}

const DAY_MS = 86_400_000;
const round2 = (n: number) => Math.round(n * 100) / 100;

const COLOR_GROUP = "#64748b"; // GK ELEKTIM (istniejący portfel) — slate
const COLOR_SIM = "#f59e0b"; // symulacja — amber
const COLOR_SUM = "#2563eb"; // suma (GK + symulacja) — primary

interface Step {
  ts: number;
  bal: number;
}
interface Row {
  ts: number;
  group: number;
  sim: number;
  sum: number;
}
interface MinStat {
  value: number;
  ts: number;
}
interface OverlayData {
  rows: Row[];
  /** Minimum globalne (najniższy punkt salda) per fala, z datą wystąpienia. */
  groupMin: MinStat | null;
  sumMin: MinStat | null;
}

function tsToLabel(ts: number): string {
  return new Date(ts).toLocaleDateString("pl", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}
function formatY(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value.toString();
}

// Saldo skumulowane po dacie: dla każdej odrębnej daty zwraca saldo PO zaksięgowaniu
// wszystkich zdarzeń z tej daty (krok schodkowy startujący od `start`).
function cumulativeByDate(events: CashflowEvent[], start: number): Step[] {
  const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));
  const out: Step[] = [];
  let run = start;
  let curDate: string | null = null;
  for (const ev of sorted) {
    run += parseFloat(ev.amount) || 0;
    if (ev.date === curDate && out.length) {
      out[out.length - 1].bal = round2(run);
    } else {
      curDate = ev.date;
      out.push({ ts: new Date(ev.date).getTime(), bal: round2(run) });
    }
  }
  return out;
}

function seriesMin(core: Row[], pick: (r: Row) => number): MinStat {
  let best = core[0];
  let bestVal = pick(core[0]);
  for (const r of core) {
    const v = pick(r);
    if (v < bestVal) {
      bestVal = v;
      best = r;
    }
  }
  return { value: bestVal, ts: best.ts };
}

function buildRows(
  simEvents: CashflowEvent[],
  groupEvents: CashflowEvent[],
  groupInitial: number,
  todayStr: string,
): OverlayData {
  const todayTs = new Date(todayStr).getTime();

  // GK: zakotwicz saldo „dziś" do realnego salda kont (jak na stronie Finanse GK):
  // saldo startowe = saldo dziś − suma zdarzeń sprzed dziś.
  const groupBefore = groupEvents
    .filter((ev) => ev.date < todayStr)
    .reduce((s, ev) => s + (parseFloat(ev.amount) || 0), 0);
  const groupStart = round2(groupInitial - groupBefore);
  // Symulacja: przyrost zlecenia liczony od zera (sam wpływ nowego zlecenia).
  const simStart = 0;

  const groupSteps = cumulativeByDate(groupEvents, groupStart);
  const simSteps = cumulativeByDate(simEvents, simStart);

  const tsSet = new Set<number>();
  for (const p of groupSteps) tsSet.add(p.ts);
  for (const p of simSteps) tsSet.add(p.ts);
  tsSet.add(todayTs);
  const tsList = [...tsSet].sort((a, b) => a - b);

  // Scalanie wskaźnikowe (obie listy posortowane) — O(n). `core` = realne daty zdarzeń + dziś.
  let gi = 0;
  let si = 0;
  let gBal = groupStart;
  let sBal = simStart;
  const core: Row[] = tsList.map((ts) => {
    while (gi < groupSteps.length && groupSteps[gi].ts <= ts) {
      gBal = groupSteps[gi].bal;
      gi++;
    }
    while (si < simSteps.length && simSteps[si].ts <= ts) {
      sBal = simSteps[si].bal;
      si++;
    }
    return { ts, group: gBal, sim: sBal, sum: round2(gBal + sBal) };
  });

  // Okno czasu = zakres dat zdarzeń symulacji (+ dziś) — TEN SAM co wykres bez nakładki.
  // GK ELEKTIM ma zdarzenia sięgające dużo dalej w przyszłość; nie rozciągamy nimi osi.
  const simTs = simEvents.map((ev) => new Date(ev.date).getTime());
  const winLo = simTs.length ? Math.min(todayTs, ...simTs) : core[0]?.ts ?? todayTs;
  const winHi = simTs.length
    ? Math.max(todayTs, ...simTs)
    : core[core.length - 1]?.ts ?? todayTs;
  const base = core.filter((r) => r.ts >= winLo && r.ts <= winHi);

  // Minimum globalne liczone w oknie zlecenia (na realnych datach), nie na ogonkach.
  const groupMin = base.length ? seriesMin(base, (r) => r.group) : null;
  const sumMin = base.length ? seriesMin(base, (r) => r.sum) : null;

  // Saldo wchodzące do okna (stan tuż przed jego początkiem) — dla płaskiego lewego ogonka.
  const balBefore = (steps: Step[], ts: number, start: number): number => {
    let b = start;
    for (const s of steps) {
      if (s.ts < ts) b = s.bal;
      else break;
    }
    return b;
  };

  const rows: Row[] = [...base];
  if (rows.length) {
    const firstTs = rows[0].ts;
    const lastTs = rows[rows.length - 1].ts;
    const span = Math.max(lastTs - firstTs, DAY_MS);
    const pad = Math.max(span * 0.04, DAY_MS * 2);
    // Bazowa linia przed oknem (płaski odcinek) i ogonek za ostatnim zdarzeniem.
    const enterGroup = balBefore(groupSteps, firstTs, groupStart);
    const enterSim = balBefore(simSteps, firstTs, simStart);
    rows.unshift({
      ts: firstTs - pad,
      group: enterGroup,
      sim: enterSim,
      sum: round2(enterGroup + enterSim),
    });
    const last = rows[rows.length - 1];
    rows.push({ ts: lastTs + pad, group: last.group, sim: last.sim, sum: last.sum });
  }
  return { rows, groupMin, sumMin };
}

function OverlayTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: Row }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const line = (label: string, value: number, color: string, strong?: boolean) => (
    <div className="flex items-center justify-between gap-4">
      <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
        <span
          className="inline-block w-2.5 h-2.5 rounded-sm"
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
      <span
        className={`tabular-nums font-semibold ${
          strong ? "text-gray-900 dark:text-gray-100" : ""
        } ${value < 0 ? "text-red-600" : ""}`}
      >
        {formatAmount(value.toFixed(2))} PLN
      </span>
    </div>
  );
  return (
    <div
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 min-w-[260px] text-sm"
      style={{ pointerEvents: "none" }}
    >
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-1.5">
        {formatDate(row.ts)}
      </p>
      <div className="space-y-1">
        {line("GK ELEKTIM (bez zlecenia)", row.group, COLOR_GROUP)}
        {line("Symulacja (samo zlecenie)", row.sim, COLOR_SIM)}
        {line("Suma (GK + zlecenie)", row.sum, COLOR_SUM, true)}
      </div>
    </div>
  );
}

export function SimGroupOverlayChart({ simEvents }: Props) {
  const { data: groupEvents = [], isLoading: eventsLoading } = useGroupCashflow();
  const { data: balances = [], isLoading: balancesLoading } = useLatestBalances();

  const groupInitial = useMemo(
    () => balances.reduce((s, b) => s + (parseFloat(b.net_balance ?? "0") || 0), 0),
    [balances],
  );

  const todayStr = new Date().toISOString().split("T")[0];
  const todayTs = new Date(todayStr).getTime();

  const { rows, groupMin, sumMin } = useMemo(
    () => buildRows(simEvents, groupEvents, groupInitial, todayStr),
    [simEvents, groupEvents, groupInitial, todayStr],
  );

  if (eventsLoading || balancesLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-400">
        <Loader2 size={16} className="animate-spin" /> Ładuję dane GK ELEKTIM…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-6 text-center">
        Brak danych do nałożenia — dodaj przepływy w symulacji lub uzupełnij salda GK.
      </p>
    );
  }

  const domain: [number, number] = [rows[0].ts, rows[rows.length - 1].ts];

  return (
    <div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
        {[
          { c: COLOR_GROUP, l: "GK ELEKTIM (bez zlecenia)" },
          { c: COLOR_SIM, l: "Symulacja (samo zlecenie)" },
          { c: COLOR_SUM, l: "Suma (GK + zlecenie)" },
        ].map((s) => (
          <span
            key={s.l}
            className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"
          >
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ backgroundColor: s.c }}
            />
            {s.l}
          </span>
        ))}
      </div>
      <div className="h-[260px] sm:h-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 16, right: 16, left: 4, bottom: 5 }}>
            <defs>
              <linearGradient id="ovGroup" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLOR_GROUP} stopOpacity={0.18} />
                <stop offset="95%" stopColor={COLOR_GROUP} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="ovSim" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLOR_SIM} stopOpacity={0.18} />
                <stop offset="95%" stopColor={COLOR_SIM} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="ovSum" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLOR_SUM} stopOpacity={0.25} />
                <stop offset="95%" stopColor={COLOR_SUM} stopOpacity={0.03} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />

            <XAxis
              type="number"
              dataKey="ts"
              scale="time"
              domain={domain}
              tickFormatter={tsToLabel}
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              tickCount={6}
            />
            <YAxis
              tickFormatter={formatY}
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={55}
            />
            <Tooltip
              content={<OverlayTooltip />}
              isAnimationActive={false}
              wrapperStyle={{ pointerEvents: "none", zIndex: 50 }}
            />

            <ReferenceLine y={0} stroke="#d1d5db" strokeWidth={1.5} />
            <ReferenceLine
              x={todayTs}
              stroke="#3b82f6"
              strokeDasharray="5 4"
              strokeWidth={1.5}
              label={{ value: "dziś", position: "top", fill: "#3b82f6", fontSize: 11 }}
            />

            {/* 3 fale — bez znaczników zdarzeń (dot={false}) */}
            <Area
              type="monotone"
              dataKey="group"
              name="GK ELEKTIM"
              stroke={COLOR_GROUP}
              strokeWidth={2}
              fill="url(#ovGroup)"
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="sim"
              name="Symulacja"
              stroke={COLOR_SIM}
              strokeWidth={2}
              fill="url(#ovSim)"
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="sum"
              name="Suma"
              stroke={COLOR_SUM}
              strokeWidth={2.75}
              fill="url(#ovSum)"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {groupMin && sumMin && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-gray-400">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: COLOR_GROUP }}
              />
              Minimum globalne GK (bez zlecenia)
            </div>
            <div
              className={`mt-1 text-lg font-bold tabular-nums ${
                groupMin.value < 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-gray-900 dark:text-gray-100"
              }`}
            >
              {formatAmount(groupMin.value.toFixed(2))} PLN
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(groupMin.ts)}
            </div>
          </div>

          <div className="rounded-lg border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-gray-400">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: COLOR_SUM }}
              />
              Minimum globalne GK (ze zleceniem)
            </div>
            <div
              className={`mt-1 text-lg font-bold tabular-nums ${
                sumMin.value < 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-gray-900 dark:text-gray-100"
              }`}
            >
              {formatAmount(sumMin.value.toFixed(2))} PLN
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(sumMin.ts)}
              {(() => {
                const delta = round2(sumMin.value - groupMin.value);
                if (delta === 0) return null;
                return (
                  <span
                    className={`ml-2 font-medium ${
                      delta < 0
                        ? "text-red-600 dark:text-red-400"
                        : "text-green-600 dark:text-green-400"
                    }`}
                  >
                    ({delta < 0 ? "" : "+"}
                    {formatAmount(delta.toFixed(2))} PLN vs bez zlecenia)
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
        GK ELEKTIM zakotwiczony do realnego salda kont na dziś. „Suma" pokazuje wpływ nowego
        zlecenia na łączny przepływ grupy. Zakres = okno symulacji (jak wykres bez nakładki);
        minimum globalne = najniższy punkt salda w tym oknie (najtrudniejszy moment płynnościowy).
      </p>
    </div>
  );
}
